/**
 * Pure wall-clock time helpers for TimedAction scheduling.
 *
 * All conversions treat seconds-of-day as local wall-clock time in a named
 * timezone — they never convert through UTC. The timezone string is stored
 * separately and passed explicitly to every function that needs it.
 */

import moment from "moment-timezone";

/**
 * Convert a seconds-of-day value (0–86399) to a moment.Moment set to
 * that wall-clock time on today's local date. The result is timezone-naive
 * and is only used to drive Ant Design TimePicker display.
 */
export function secondsOfDayToMoment(seconds: number): moment.Moment {
  // Set the clock fields: adding elapsed seconds is an hour out on a day the clocks change.
  return moment()
    .startOf("day")
    .hours(Math.floor(seconds / 3600))
    .minutes(Math.floor((seconds % 3600) / 60))
    .seconds(seconds % 60);
}

/**
 * Convert a moment.Moment (from a TimePicker) back to seconds-of-day using
 * the wall-clock hours/minutes/seconds only. Does NOT convert through UTC.
 */
export function momentToSecondsOfDay(m: moment.Moment): number {
  return m.hours() * 3600 + m.minutes() * 60 + m.seconds();
}

/**
 * Resolve a usable IANA zone, falling back to the browser's local zone when
 * the stored timezone is empty or unknown. The absolute start_at/stop_at
 * windows (ONE_SHOT / INTERVAL) do NOT render the Timezone field, so the
 * resolved tz is frequently "" — and `moment.tz(date, "")` silently treats an
 * empty/unknown zone as UTC, which shifts a locally-picked wall-clock time by
 * the UTC offset (e.g. picking 1:30pm landed the window hours in the past).
 * Defaulting to the local zone keeps the picked wall-clock time honest.
 */
function resolveZone(tz: string): string {
  return tz && moment.tz.zone(tz) ? tz : moment.tz.guess();
}

/**
 * Interpret a picker's wall-clock value in the given named timezone and
 * return an ISO 8601 string. For example, if the user picked "2026-01-15
 * 09:00" and tz="America/New_York", the result is an ISO string for 9am
 * Eastern that day. An empty/unknown tz falls back to the browser-local zone.
 */
export function localDateTimeToISO(m: moment.Moment, tz: string): string {
  // Format the picked value as a local datetime string, then reparse it in
  // the target timezone — this avoids any implicit UTC conversion.
  return moment.tz(m.format("YYYY-MM-DD HH:mm:ss"), resolveZone(tz)).toISOString();
}

/**
 * Parse an ISO 8601 string into a moment in the given named timezone so
 * the DatePicker/TimePicker can display the wall-clock value in that zone.
 * An empty/unknown tz falls back to the browser-local zone.
 */
export function isoToLocalMoment(iso: string, tz: string): moment.Moment {
  return moment.tz(iso, resolveZone(tz));
}

/**
 * Return true when the stop time falls on the next calendar day relative to
 * the start time. This is the case whenever stop <= start (e.g., 08:00–06:00
 * crosses midnight).
 */
export function deriveStopNextDay(startSeconds: number, stopSeconds: number): boolean {
  return stopSeconds <= startSeconds;
}
